import { Suspense, lazy, type ComponentType, type ReactNode } from "react";
import { LayoutDashboard, ExternalLink, ShieldX } from "lucide-react";
import { Outlet, ScrollRestoration, createBrowserRouter } from "react-router";
import { hasPermission, useSession } from "@pmo/api-client";
import { useTranslation } from "@pmo/i18n";
import { ForbiddenPage, LoginPage, NotFoundPage, RequireAuth, RouteErrorPage, ShellLayout, type ShellNavSection } from "@pmo/shell";
import { Button, EmptyState, LoadingRegion, SkeletonCard, toneFor } from "@pmo/ui";
import { AREAS, BACKOFFICE_URL, GROUPS } from "./sections";

const Overview = lazy(() => import("../features/overview/OverviewPage"));

const el = (C: ComponentType) => (
  <Suspense
    fallback={
      <LoadingRegion className="min-h-64">
        <SkeletonCard />
      </LoadingRegion>
    }
  >
    <C />
  </Suspense>
);

function Root() {
  return (
    <>
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

function hasConsoleAccess(permissions: ReadonlySet<string>) {
  return [...permissions].some((p) => p === "*" || p.startsWith("console."));
}

function NoConsoleAccess() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <div className="max-w-lg">
        <EmptyState
          icon={ShieldX}
          title={t("common:errors.forbiddenTitle")}
          description={t("noAccess")}
          action={
            <Button asChild>
              <a href={BACKOFFICE_URL}>
                {t("nav.backoffice")}
                <ExternalLink aria-hidden />
              </a>
            </Button>
          }
        />
      </div>
    </div>
  );
}

function ConsoleLayout() {
  const { t } = useTranslation();
  const { permissions } = useSession();
  if (!hasConsoleAccess(permissions)) return <NoConsoleAccess />;
  const sections: ShellNavSection[] = [
    { items: [{ key: "overview", label: t("nav.overview"), icon: LayoutDashboard, tone: "gold", to: "/", goKey: "h" }] },
    ...GROUPS.map((g) => ({
      key: g,
      title: t(`nav.${g}`),
      items: AREAS.filter((a) => a.group === g && hasPermission(permissions, a.permission)).map((a) => ({ key: a.key, label: t(`nav.${a.key}`), icon: a.icon, tone: toneFor(a.key), to: `/${a.path}` })),
    })).filter((s) => s.items.length),
    { items: [{ key: "backoffice", label: t("nav.backoffice"), icon: ExternalLink, to: BACKOFFICE_URL, external: true }] },
  ];
  return <ShellLayout platformName={t("common:app.consoleName")} brandSubtitle={t("common:app.consoleName")} description={t("common:app.consoleDescription")} sections={sections} sidebarKey="pmo.console.sidebar" classification="internal" notifications={false} profileHref="/" />;
}

function ConsoleLogin() {
  const { t } = useTranslation();
  return <LoginPage appName={t("common:app.consoleName")} preferKinds={["platform"]} />;
}

function Guarded({ permission, children }: { permission: string; children: ReactNode }) {
  const { permissions } = useSession();
  return hasPermission(permissions, permission) ? <>{children}</> : <ForbiddenPage />;
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: "/login", element: <ConsoleLogin /> },
      {
        element: (
          <RequireAuth>
            <ConsoleLayout />
          </RequireAuth>
        ),
        errorElement: <RouteErrorPage />,
        handle: { crumb: "nav.overview" },
        children: [
          { index: true, element: el(Overview) },
          ...AREAS.map((a) => {
            const Page = lazy(a.load);
            return { path: a.path, handle: { crumb: `nav.${a.key}` }, element: <Guarded permission={a.permission}>{el(Page)}</Guarded> };
          }),
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
